import dayjs from 'dayjs'
import {Button} from '@/components/ui/button/Button'
import {AlertCircleIcon, ArrowLeftIcon, Copy01Icon, Download01Icon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {TKey, useTranslation} from '@/translations/i18n'
import {patientServiceTypeMapToLabel} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {SiarExportPreviewItem, SiarExportProgressStatus} from '@/features/projects/siar-bulk-export/types'
import {HttpGetSiarBulkExportPreviewCountersResponse} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {BulkExportConfig} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportConfigStep'
import {PreviewFilterTab} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportDialog'
import {BulkSiarExportCriteriaSummary} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportCriteriaSummary'
import {BulkSiarExportPreviewTable} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTable'
import {BulkSiarExportPreviewTableSkeleton} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTableSkeleton'
import {BulkSiarExportInOtherBatchBanner} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportInOtherBatchBanner'
import {
    StyledBulkSiarExportBackButton,
    StyledBulkSiarExportExcludedBanner,
    StyledBulkSiarExportExcludedBannerText,
    StyledBulkSiarExportExcludedTabIcon,
    StyledBulkSiarExportFilterTab,
    StyledBulkSiarExportFilterTabBar,
    StyledBulkSiarExportPreviewBody,
    StyledBulkSiarExportPreviewCounterRow,
    StyledBulkSiarExportPreviewEmptyState,
    StyledBulkSiarExportPreviewFooter,
    StyledBulkSiarExportToggleAllButton
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

// Per-status tab label keys (the status value also doubles as the tab id / preview `status` filter).
const statusToTabLabelKey = {
    to_print: 'projects:bulk_siar_export_preview_tab_to_print',
    to_export_opening: 'projects:bulk_siar_export_preview_tab_to_export_opening',
    to_transmit_opening: 'projects:bulk_siar_export_preview_tab_to_transmit_opening',
    to_export_closing: 'projects:bulk_siar_export_preview_tab_to_export_closing',
    to_transmit_closing: 'projects:bulk_siar_export_preview_tab_to_transmit_closing'
} as const satisfies Record<SiarExportProgressStatus, TKey>

type BulkSiarExportPreviewStepProps = {
    config: BulkExportConfig
    items: SiarExportPreviewItem[]
    total: number
    isLoading: boolean
    counters: HttpGetSiarBulkExportPreviewCountersResponse | undefined
    isCountersLoading: boolean
    activeTab: PreviewFilterTab
    onTabChange: (tab: PreviewFilterTab) => void
    selectableStatuses: SiarExportProgressStatus[]
    isSelected: (item: SiarExportPreviewItem) => boolean
    selectedCount: number
    viewSelectedCount: number
    viewEligibleTotal: number
    onMasterToggle: () => void
    onToggle: (item: SiarExportPreviewItem) => void
    onIncludeOtherBatch: () => void
    onIgnoreOtherBatch: () => void
    selectedInOtherBatchCount: number
    onLoadMore: () => void
    onBack: () => void
    onExport: () => void
    isExportPending: boolean
}

export const BulkSiarExportPreviewStep = ({
    config,
    items,
    isLoading,
    counters,
    isCountersLoading,
    activeTab,
    onTabChange,
    selectableStatuses,
    isSelected,
    selectedCount,
    viewSelectedCount,
    viewEligibleTotal,
    onMasterToggle,
    onToggle,
    onIncludeOtherBatch,
    onIgnoreOtherBatch,
    selectedInOtherBatchCount,
    onLoadMore,
    onBack,
    onExport,
    isExportPending
}: BulkSiarExportPreviewStepProps) => {
    const {t} = useTranslation()

    const allDeselected = viewSelectedCount === 0

    // Master checkbox reflects loaded items only
    const loadedSelectedCount = items.filter(isSelected).length
    const masterChecked = items.length > 0 && loadedSelectedCount === items.length
    const masterIndeterminate = loadedSelectedCount > 0 && loadedSelectedCount < items.length

    const isInitialLoading = isLoading && items.length === 0
    const showCounters = !isCountersLoading && !!counters
    const showBanner = selectedInOtherBatchCount > 0
    const isClosingExport = config.exportType === 'close_project'
    const isExcludedView = activeTab === 'excluded'

    // Per-status counts read from the per-phase counters payload.
    const statusCount: Record<SiarExportProgressStatus, number> = {
        to_print: counters?.toPrint ?? 0,
        to_export_opening: counters?.toExportOpening ?? 0,
        to_transmit_opening: counters?.toTransmitOpening ?? 0,
        to_export_closing: counters?.toExportClosing ?? 0,
        to_transmit_closing: counters?.toTransmitClosing ?? 0
    }

    // Copy every excluded project currently shown in the list: one PRI per line, with its
    // info comma-separated — code (no "PRI-" prefix), patient name, service type, start date.
    const handleCopyExcludedIds = () => {
        const text = items
            .map(item => {
                const code = String(item.projectCode).padStart(4, '0')
                const serviceType = item.serviceType ? t(patientServiceTypeMapToLabel[item.serviceType]) : '—'
                const startDate = item.startDate ? dayjs(item.startDate).format('DD/MM/YYYY') : '—'
                return [code, item.patientFullName, serviceType, startDate].join(', ')
            })
            .join('\n')
        navigator.clipboard
            .writeText(text)
            .then(() => toast.success(t('projects:bulk_siar_export_excluded_copy_success')))
            .catch(() => toast.error(t('errors:default')))
    }

    return (
        <>
            <StyledBulkSiarExportPreviewBody>
                <BulkSiarExportCriteriaSummary config={config} />
                {showBanner && (
                    <BulkSiarExportInOtherBatchBanner
                        count={selectedInOtherBatchCount}
                        onInclude={onIncludeOtherBatch}
                        onIgnore={onIgnoreOtherBatch}
                    />
                )}
                {showCounters && !isExcludedView && (
                    <StyledBulkSiarExportPreviewCounterRow>
                        <span>
                            {t('projects:bulk_siar_export_selected_counter', {
                                selected: viewSelectedCount,
                                total: viewEligibleTotal
                            })}
                        </span>
                        {viewEligibleTotal > 0 && (
                            <StyledBulkSiarExportToggleAllButton type="button" onClick={onMasterToggle}>
                                {allDeselected
                                    ? t('projects:bulk_siar_export_select_all')
                                    : t('projects:bulk_siar_export_deselect_all')}
                            </StyledBulkSiarExportToggleAllButton>
                        )}
                    </StyledBulkSiarExportPreviewCounterRow>
                )}
                <StyledBulkSiarExportFilterTabBar>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'all'}
                        onClick={() => onTabChange('all')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_all')}
                        {showCounters && ` (${counters.total})`}
                    </StyledBulkSiarExportFilterTab>
                    {selectableStatuses.map(status => (
                        <StyledBulkSiarExportFilterTab
                            key={status}
                            type="button"
                            $active={activeTab === status}
                            onClick={() => onTabChange(status)}
                        >
                            {t(statusToTabLabelKey[status])}
                            {showCounters && ` (${statusCount[status]})`}
                        </StyledBulkSiarExportFilterTab>
                    ))}
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={isExcludedView}
                        $danger
                        onClick={() => onTabChange('excluded')}
                    >
                        <StyledBulkSiarExportExcludedTabIcon>
                            <AlertCircleIcon size={14} />
                        </StyledBulkSiarExportExcludedTabIcon>
                        {t('projects:bulk_siar_export_preview_tab_excluded')}
                        {showCounters && ` (${counters.excluded})`}
                    </StyledBulkSiarExportFilterTab>
                </StyledBulkSiarExportFilterTabBar>
                {isExcludedView && items.length > 0 && (
                    <StyledBulkSiarExportExcludedBanner>
                        <StyledBulkSiarExportExcludedBannerText>
                            <AlertCircleIcon size={18} />
                            <span>
                                {t(
                                    isClosingExport
                                        ? 'projects:bulk_siar_export_excluded_banner_closing'
                                        : 'projects:bulk_siar_export_excluded_banner'
                                )}
                            </span>
                        </StyledBulkSiarExportExcludedBannerText>
                        <Button
                            variant="secondaryDanger"
                            size="sm"
                            onClick={handleCopyExcludedIds}
                            style={{flexShrink: 0}}
                        >
                            <Copy01Icon size={16} />
                            {t('projects:bulk_siar_export_excluded_copy_ids')}
                        </Button>
                    </StyledBulkSiarExportExcludedBanner>
                )}
                {isInitialLoading ? (
                    <BulkSiarExportPreviewTableSkeleton />
                ) : items.length === 0 ? (
                    <StyledBulkSiarExportPreviewEmptyState>
                        {t('projects:bulk_siar_export_preview_empty')}
                    </StyledBulkSiarExportPreviewEmptyState>
                ) : (
                    <BulkSiarExportPreviewTable
                        items={items}
                        isSelected={isSelected}
                        onToggle={onToggle}
                        onToggleAll={onMasterToggle}
                        masterChecked={masterChecked}
                        masterIndeterminate={masterIndeterminate}
                        onLoadMore={onLoadMore}
                        readOnly={isExcludedView}
                    />
                )}
            </StyledBulkSiarExportPreviewBody>
            <StyledBulkSiarExportPreviewFooter>
                <StyledBulkSiarExportBackButton type="button" onClick={onBack}>
                    <ArrowLeftIcon />
                    {t('projects:bulk_siar_export_back')}
                </StyledBulkSiarExportBackButton>
                <Button variant="primaryNeutral" onClick={onExport} disabled={selectedCount === 0 || isExportPending}>
                    <Download01Icon />
                    {t('projects:bulk_siar_export_export_cta', {count: selectedCount})}
                    {isExportPending && <Spinner />}
                </Button>
            </StyledBulkSiarExportPreviewFooter>
        </>
    )
}
